/**
 * Object for creating click-triggered navigation submenus
 *
 * Based on:
 * - https://codepen.io/mrwweb/pen/pXqKZO
 * - https://css-tricks.com/in-praise-of-the-unambiguous-click-menu/
 */

(function () {
	"use strict";

	const ClickyMenus = function (menu) {
		// DOM element(s)
		let container = menu,
			currentMenuItems = {},
			i,
			len;

		this.init = function () {
			menuSetup();
			document.addEventListener("click", closeOpenMenus);
		};

		/*===================================================
		=            Menu Open / Close Functions            =
		===================================================*/
		function toggleOnFocusOut(button) {
			document.addEventListener("focusin", (event) => {
				if (!button.parentElement.contains(document.activeElement) && button.getAttribute("aria-expanded") === "true") {
					toggleSubmenu(button);
				}
			});
		}

		function toggleOnMenuClick(event) {
			let button = event.currentTarget;
			let submenu = document.getElementById(button.getAttribute("aria-controls"));
			let level = submenu.getAttribute("data-level");

			// Check if there is already an open menu, and make sure the current menu being toggled isn't this one
			if (currentMenuItems.hasOwnProperty(level) && button.id !== currentMenuItems[level]) {
				// Get the button and submenu for the already open menu
				let openButton = document.getElementById(currentMenuItems[level]);
				let openSubmenu = document.getElementById(openButton.getAttribute("aria-controls"));

				if (window.getComputedStyle(openSubmenu).position === "absolute") {
					// Close it if it's absolutely positioned
					toggleSubmenu(openButton);
				}
			}

			toggleSubmenu(button);
		}

		function toggleSubmenu(button) {
			let expanded = button.getAttribute("aria-expanded");
			let submenu = document.getElementById(button.getAttribute("aria-controls"));
			let level = submenu.getAttribute("data-level");

			if (expanded === "true") {
				button.setAttribute("aria-expanded", false);
				submenu.setAttribute("aria-hidden", true);
				makeElementsFocusable(submenu.focusableElements, true);
				button.parentElement.classList.remove("has-open-submenu");
				delete currentMenuItems[level];
			} else {
				button.setAttribute("aria-expanded", true);
				submenu.setAttribute("aria-hidden", false);
				makeElementsFocusable(submenu.focusableElements, false);
				button.parentElement.classList.add("has-open-submenu");
				toggleOnFocusOut(button, submenu);
				currentMenuItems[level] = button.id;
			}
		}

		function makeElementsFocusable(elements, focusable) {
			elements.forEach(function (element) {
				if (focusable === false) {
					element.removeAttribute("tabindex");
				} else {
					element.setAttribute("tabindex", "-1");
				}
			});
		}

		function closeOnEscKey(event) {
			if (27 === event.keyCode) {
				if (null !== event.target.closest('[aria-hidden="false"]')) {
					// we're in a submenu item
					let button = document.getElementById(
						event.target.closest('[aria-hidden="false"]').getAttribute("data-toggled-by")
					);

					button.focus();
					toggleSubmenu(button);
				} else if ("true" === event.target.getAttribute("aria-expanded")) {
					// we're on a parent item
					toggleSubmenu(event.target);
				}
			}
		}

		function closeOpenMenus(event) {
			if (!container.id) {
				return false;
			}

			if (!event.target.closest("#" + container.id)) {
				let activeItems = Object.values(currentMenuItems);
				if (activeItems.length > 0) {
					let target = document.getElementById(activeItems[0]);
					toggleSubmenu(target);
				}
			}
		}

		/*===========================================================
		=            Modify Menu Markup & Bind Listeners            =
		=============================================================*/
		function menuSetup() {
			menu.querySelectorAll("[data-toggled-by]").forEach(function (submenu) {
				if ("undefined" !== typeof submenu) {
					const menuItem = submenu.parentElement;

					let buttonId;
					buttonId = submenu.getAttribute("data-toggled-by");

					let button = menuItem.querySelector("#" + buttonId);

					if (button.matches("a, span")) {
						button = convertElementToButton(button);
					} else {
						return;
					}

					setUpAria(submenu, button);

					// set child item focus
					submenu.focusableElements = getFocusableElements(submenu);
					makeElementsFocusable(submenu.focusableElements, false);

					// bind event listener to button
					button.addEventListener("click", toggleOnMenuClick);
					menu.addEventListener("keydown", closeOnEscKey);
					if (submenu.getAttribute("aria-hidden") == "false") {
						let level = submenu.getAttribute("data-level");
						currentMenuItems[level] = button.id;
					}
				}
			});
		}

		function setUpAria(submenu, button) {
			const menuItemId = submenu.getAttribute("data-toggled-by");
			const submenuId = submenu.getAttribute("id");
			const expandedByDefault = submenu.getAttribute("data-aria-expanded-default");

			let id;
			if (null === submenuId) {
				id = button.textContent.trim().replace(/\s+/g, "-").toLowerCase() + "-submenu";
			} else {
				id = menuItemId;
			}

			let expanded;
			let hidden;
			if (null === expandedByDefault) {
				expanded = false;
			} else {
				expanded = true;
			}

			// set button ARIA
			button.setAttribute("aria-controls", submenuId);
			button.setAttribute("aria-expanded", expanded);

			// set submenu ARIA
			submenu.setAttribute("aria-hidden", !expanded);

			// remove setup attributes for cleaner markup
			submenu.removeAttribute("data-aria-expanded-default");
		}

		/**
		 * Get focusable elements within a container
		 * @source Andy Bell?
		 * @param  {Element} parent The HTML Element
		 * @return {Boolean}
		 */
		function getFocusableElements(parent) {
			if (!parent) {
				console.warn("You need to pass a parent HTMLElement");
				return [];
			}

			return parent.querySelectorAll(
				'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled]), details:not([disabled]), summary:not(:disabled)'
			);
		}

		/**
		 * Convert an HTML element to a button
		 * @url https://justmarkup.com/articles/2019-01-21-the-link-to-button-enhancement/
		 * @param  {Element} element The HTML Element
		 * @return {Boolean}
		 */
		function convertElementToButton(element) {
			if (null !== element) {
				const elementHTML = element.innerHTML;
				const elementAtts = element.attributes;
				const button = document.createElement("button");

				// copy button attributes and content from link
				button.innerHTML = elementHTML.trim();
				for (i = 0, len = elementAtts.length; i < len; i++) {
					let attr = elementAtts[i];
					if ("href" !== attr.name) {
						button.setAttribute(attr.name, attr.value);
					}
				}
				element.replaceWith(button);

				return button;
			}

			return element;
		}
	};

	function initializeDropdowns(target) {
		const menus = document.querySelectorAll(target);

		menus.forEach(function (menu) {
			if (menu.innerHTML.includes("data-toggled-by")) {
				let clickyMenu = new ClickyMenus(menu);
				clickyMenu.init();
			}
		});
	}

	/* Create a ClickMenus object and initiate menu for any menu containing "aria-control" */
	document.addEventListener("DOMContentLoaded", function () {
		initializeDropdowns("nav[data-toggle]");
	});
})();
